<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { peopleApi } from '$lib/api/crm';
  import { leadsApi } from '$lib/api/leads';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { roleProfile, workflowAccessKey } from '$lib/utils/roleProfile';
  import { dayInIndia } from '$lib/utils/myDay';
  import { relativeDay } from '$lib/utils/touchpoints';

  // Administrator oversight on the Dashboard: work nobody is responsible for yet.
  const profile = $derived(roleProfile(session.me));
  const accessKey = $derived(workflowAccessKey(session.me));
  const vertical = $derived(session.selectedVertical);
  const verticalId = $derived(vertical === 'all' ? undefined : vertical);
  const scopeName = $derived(vertical === 'all' ? 'all teams' : session.me?.verticals.find(team => team.id === vertical)?.name || 'this team');
  const unassigned = createQuery(() => ({
    queryKey: ['my-day-unassigned-contacts', profile, session.me?.user.id, vertical, accessKey], enabled: profile === 'oversight',
    queryFn: () => peopleApi.list({ owner_id: 'unassigned', vertical_id: verticalId, per_page: 5, page: 1 })
  }));
  const leads = createQuery(() => ({
    queryKey: ['dashboard-new-leads', profile, session.me?.user.id, vertical, accessKey], enabled: profile === 'oversight',
    queryFn: () => leadsApi.list({ vertical_id: verticalId, status: 'new', spam: 'false', exclude_download_only: 'true', per_page: 5, page: 1 }),
    staleTime: 60000, refetchOnWindowFocus: true
  }));
</script>

{#if profile === 'oversight'}
  <div class="grid items-start gap-5 lg:grid-cols-2">
    <section class="day-card" aria-labelledby="cleanup">
      <h2 id="cleanup" class="day-title">Contacts with no owner{#if unassigned.isSuccess} · {unassigned.data.count}{/if}</h2>
      <p class="day-meta mb-2 mt-1">Nobody is responsible for these yet in {scopeName}.</p>
      {#if unassigned.isPending}<p role="status" class="day-meta">Loading…</p>
      {:else if unassigned.isError}<p role="alert" class="text-sm text-red-700">{getErrorMessage(unassigned.error)}</p><button class="day-mini-btn mt-2" onclick={() => unassigned.refetch()}>Retry</button>
      {:else}
        {#each unassigned.data?.results ?? [] as person (person.id)}
          <div class="day-row"><div class="min-w-0 flex-1"><p class="truncate font-semibold text-navy">{person.first_name} {person.last_name}</p><p class="text-xs text-dark-gray">{session.me?.verticals.find(team => team.id === person.vertical_id)?.name ?? 'Pipeline'}</p></div><button class="day-mini-btn" onclick={() => route.push({ tab: 'people', id: person.id })}>Assign owner</button></div>
        {:else}<p class="rounded-xl bg-[#ecf8cc] p-3 text-sm text-[#2e4400]">Every contact has an owner.</p>{/each}
        {#if (unassigned.data?.count ?? 0) > (unassigned.data?.results.length ?? 0)}<button class="day-link mt-3 inline-block" onclick={() => route.push({ tab: 'people', filters: { view: 'unassigned' } })}>All {unassigned.data?.count} without an owner →</button>{/if}
      {/if}
    </section>
    <section class="day-card" aria-labelledby="new-enquiries">
      <h2 id="new-enquiries" class="day-title">New enquiries{#if leads.isSuccess} · {leads.data.count}{/if}</h2>
      <p class="day-meta mb-2 mt-1">Not yet triaged or handed to a salesperson.</p>
      {#if leads.isPending}<p role="status" class="day-meta">Loading…</p>
      {:else if leads.isError}<p role="alert" class="text-sm text-red-700">{getErrorMessage(leads.error)}</p><button class="day-mini-btn mt-2" onclick={() => leads.refetch()}>Retry</button>
      {:else}
        {#each leads.data?.results ?? [] as lead (lead.id)}<div class="day-row"><div class="min-w-0 flex-1"><p class="truncate font-semibold text-navy">{lead.name || lead.email || `Lead #${lead.id}`}</p><p class="truncate text-xs text-dark-gray">{lead.category || 'Uncategorised'} · {dayInIndia(lead.created_at) ? `received ${relativeDay(lead.created_at).toLowerCase()}` : 'date unavailable'}</p></div><button class="day-mini-btn" onclick={() => route.push({ tab: 'leads', id: lead.id })}>Open</button></div>
        {:else}<p class="rounded-xl bg-[#ecf8cc] p-3 text-sm text-[#2e4400]">No new enquiries waiting.</p>{/each}
      {/if}
    </section>
  </div>
{/if}
